import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ShoppingCart, Clock, CheckCircle2, XCircle, RotateCw, Ban, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";

const fmt = (v) => `R$ ${(v || 0).toFixed(2).replace(".", ",")}`;
const fmtDate = (s) => s ? new Date(s).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "";

const STATUS = {
  pending: { icon: Clock, color: "text-amber-700 bg-amber-50 border-amber-200", label: "Aguardando pagamento" },
  paid: { icon: CheckCircle2, color: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Pago" },
  failed: { icon: XCircle, color: "text-red-700 bg-red-50 border-red-200", label: "Pagamento falhou" },
  cancelled: { icon: Ban, color: "text-stone-600 bg-stone-100 border-stone-200", label: "Cancelado" },
};

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const { loadFromOrder, cart } = useCart() || {};
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/orders/mine").then((r) => setOrders(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const retry = async (id) => {
    try {
      const { data } = await api.post(`/orders/${id}/retry-payment`);
      const url = data.init_point || data.sandbox_init_point;
      if (url) window.location.href = url;
      else toast.error("Sem URL de pagamento");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancelar este pedido? Se houver BemCash usado, ele será devolvido.")) return;
    try { await api.post(`/orders/${id}/cancel`); toast.success("Pedido cancelado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const editPending = async (o) => {
    // Restaura o pedido pendente no carrinho local (cancela o pedido atual no servidor)
    if (cart && cart.companyId !== o.company_id) {
      if (!window.confirm(`Seu carrinho tem itens de "${cart.companyName}". Substituir pelos itens deste pedido de "${o.company_name}"?`)) return;
    }
    try {
      await api.post(`/orders/${o.id}/cancel`);
    } catch (e) { /* ok se já estiver cancelado */ }
    loadFromOrder(o);
    toast.success("Pedido aberto no carrinho — adicione mais itens se quiser");
    nav("/checkout");
  };

  const list = orders.filter((o) =>
    (filter === "all" || o.status === filter) &&
    (!search || o.company_name?.toLowerCase().includes(search.toLowerCase()) || o.id.startsWith(search))
  );

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
      <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-4xl font-bold text-stone-900 flex items-center gap-2"><ShoppingCart size={22} className="sm:hidden"/><ShoppingCart size={28} className="hidden sm:inline"/> Minhas compras</h1>
          <p className="text-stone-500 text-xs sm:text-sm mt-0.5 sm:mt-1">Pedidos feitos pelo Checkout MorarBem.</p>
        </div>
        <Link to="/cidades" className="text-xs sm:text-sm text-blue-900 hover:text-blue-950">← Continuar explorando</Link>
      </div>

      <Card className="p-2.5 sm:p-3 rounded-2xl border-stone-200 mb-4 sm:mb-6 flex items-center gap-2 flex-wrap">
        <Input placeholder="Buscar por loja ou id..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-xl flex-1 min-w-[140px] h-9 text-sm" data-testid="orders-search"/>
        <div className="flex gap-1 overflow-x-auto">
          {["all","pending","paid","cancelled","failed"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`px-2.5 py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold whitespace-nowrap ${filter===s ? "bg-blue-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`} data-testid={`orders-filter-${s}`}>
              {s==="all"?"Todos":STATUS[s]?.label||s}
            </button>
          ))}
        </div>
      </Card>

      {loading ? <p className="text-stone-500 text-sm">Carregando...</p> :
       list.length === 0 ? <Card className="p-8 sm:p-10 text-center rounded-2xl border-stone-200" data-testid="empty-orders"><ShoppingCart className="mx-auto text-stone-300" size={42}/><p className="font-display text-lg sm:text-xl mt-3">Nenhum pedido aqui</p><p className="text-stone-500 text-xs sm:text-sm mt-1">Explore lojas e finalize uma compra.</p></Card> :
       <div className="space-y-2 sm:space-y-3">
        {list.map((o) => {
          const isConfirming = o.status === "pending" && o.awaiting_confirmation;
          const st = isConfirming
            ? { icon: Clock, color: "text-amber-800 bg-amber-100 border-amber-300", label: "Confirmando pagamento" }
            : (STATUS[o.status] || STATUS.pending);
          const Icon = st.icon;
          return (
            <Card key={o.id} className={`p-3 sm:p-4 rounded-2xl border ${st.color.includes("border") ? st.color.split(" ").filter(c => c.startsWith("border")).join(" ") : "border-stone-200"} bg-white`} data-testid={`order-card-${o.id}`}>
              <div className="flex items-start justify-between gap-2 sm:gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider px-1.5 sm:px-2 py-0.5 rounded ${st.color}`}><Icon size={10}/> {st.label}</span>
                    <span className="text-[10px] sm:text-[11px] text-stone-400 font-mono">#{o.id.slice(0, 8)}</span>
                    <span className="text-[10px] sm:text-[11px] text-stone-500">{fmtDate(o.created_at)}</span>
                  </div>
                  <h3 className="font-display text-base sm:text-lg font-bold text-stone-900 mt-1 line-clamp-1">{o.company_name}</h3>
                  <p className="text-[11px] sm:text-xs text-stone-600">{o.items?.length || 0} ite{(o.items?.length||0)===1?"m":"ns"} · Sub {fmt(o.subtotal)}</p>
                  <div className="text-[10px] sm:text-xs text-stone-600 mt-1 flex gap-2 sm:gap-3 flex-wrap">
                    {o.cashback_used > 0 && <span className="text-emerald-700">BemCash: -{fmt(o.cashback_used)}</span>}
                    {o.mp_amount > 0 && <span>MP: {fmt(o.mp_amount)}</span>}
                    {o.customer_gateway_fee > 0 && <span className="text-amber-700">Taxa: {fmt(o.customer_gateway_fee)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                  {o.awaiting_confirmation && o.status === "pending" && (
                    <span className="text-[10px] sm:text-xs px-2 py-1 rounded-md bg-amber-100 text-amber-900 border border-amber-300 font-bold" data-testid={`awaiting-${o.id}`}>Confirmando pagamento</span>
                  )}
                  {o.status === "pending" && !o.awaiting_confirmation && (
                    <Button size="sm" variant="outline" onClick={() => editPending(o)} className="rounded-xl text-blue-900 border-blue-300 text-[11px] sm:text-xs h-8" data-testid={`edit-${o.id}`}>
                      <Pencil size={12} className="mr-1"/> Editar
                    </Button>
                  )}
                  {o.status === "pending" && !o.awaiting_confirmation && (o.mp_charge || o.mp_amount) > 0 && (
                    <Button size="sm" onClick={() => retry(o.id)} className="bg-amber-500 hover:bg-amber-600 text-amber-950 rounded-xl font-bold text-[11px] sm:text-xs h-8" data-testid={`retry-${o.id}`}>
                      <RotateCw size={12} className="mr-1"/> Pagar
                    </Button>
                  )}
                  {o.status === "pending" && !o.awaiting_confirmation && (
                    <Button size="sm" variant="ghost" onClick={() => cancel(o.id)} className="rounded-xl text-stone-500 hover:text-red-700 text-[11px] sm:text-xs h-8" data-testid={`cancel-${o.id}`}>
                      <Ban size={12}/>
                    </Button>
                  )}
                  {o.status === "paid" && o.fulfillment_status && (
                    <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-1 rounded-md bg-blue-50 text-blue-900 border border-blue-200">{o.fulfillment_status}</span>
                  )}
                </div>
              </div>
              {o.items?.length > 0 && (
                <details className="mt-2 sm:mt-3">
                  <summary className="text-[11px] sm:text-xs text-stone-500 hover:text-stone-700 cursor-pointer">Ver itens</summary>
                  <ul className="mt-2 text-[11px] sm:text-xs text-stone-600 space-y-1">
                    {o.items.map((it) => <li key={it.product_id}>{it.qty}× {it.title} — {fmt(it.total)}</li>)}
                  </ul>
                </details>
              )}
            </Card>
          );
        })}
       </div>}
    </div>
  );
}
